import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { QuestionReviewComponent } from './components/question-review/question-review.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { PlacementService } from '../../core/services/placement-service/placement-service.service';
import { TestResultData, AttemptByTestId, GetAttemptsByTestIdResponse } from '../../core/models/placement/placement.model';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-test-result',
    standalone: true,
    imports: [CommonModule, QuestionReviewComponent, MatIconModule, MatButtonModule, MatMenuModule
	],
    templateUrl: './test-result.component.html',
    styleUrls: ['./test-result.component.scss']
})

export class TestResultComponent implements OnInit {
    resultData: TestResultData | null = null;
    isLoading: boolean = false;
    testId: string | null = null;
    allAttemptsData: GetAttemptsByTestIdResponse['Data'] | null = null;
    selectedAttemptIndex: number = 0;
    hasMultipleAttempts: boolean = false;
    isPaid: boolean = false;
    private destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private placementService: PlacementService,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        const state = window.history.state;

        if (state && state.allAttemptsData) {
            this.allAttemptsData = state.allAttemptsData;
            this.hasMultipleAttempts = state.allAttemptsData.attempts.length > 1;

            const selectedAttemptId = state.selectedAttemptId;
            if (selectedAttemptId && this.allAttemptsData) {
                const index = this.allAttemptsData.attempts.findIndex(a => a.attempt_id === selectedAttemptId);
                if (index !== -1) {
                    this.selectedAttemptIndex = index;
                }
            }

            if (this.allAttemptsData && this.allAttemptsData.attempts[this.selectedAttemptIndex]) {
                const selectedAttempt = this.allAttemptsData.attempts[this.selectedAttemptIndex];
                this.resultData = {
                    attempt_id: selectedAttempt.attempt_id,
                    test_id: this.allAttemptsData.test_id,
                    student_id: this.allAttemptsData.student_id,
                    student_name: this.allAttemptsData.student_name,
                    test_type: this.allAttemptsData.test_type,
                    status: selectedAttempt.status,
                    finished_at: selectedAttempt.finished_at,
                    time_taken: selectedAttempt.time_taken,
                    is_paid: this.allAttemptsData.is_paid,
                    overall_performance: selectedAttempt.overall_performance,
                    test_conversation: selectedAttempt.test_conversation,
                    retake_permitted: this.allAttemptsData.retake_permitted
                };
                this.isPaid = this.resultData.is_paid || false;
            }
        }

        else if (state && state.resultData) {
            this.resultData = state.resultData;
            this.testId = state.testId;
            this.isPaid = this.resultData?.is_paid ?? false;

            if (this.testId) {
                this.fetchAllAttempts();
            }
        } else {
            console.warn('No result data found in state');
        }
    }
    fetchAllAttempts(): void {
        if (!this.testId) return;

        this.isLoading = true;
        this.placementService.getAttemptsByTestId(this.testId, 1, 10)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    this.isLoading = false;
                    if (res.Success && res.Data) {
                        this.allAttemptsData = res.Data;
                        this.isPaid = this.allAttemptsData.is_paid;
                        this.hasMultipleAttempts = res.Data.attempts.length > 1;

                        const currentAttemptId = this.resultData?.attempt_id;
                        if (currentAttemptId) {
                            const index = res.Data.attempts.findIndex(a => a.attempt_id === currentAttemptId);
                            if (index !== -1) {
                                this.selectedAttemptIndex = index;
                            }
                        }
                    }
                },
                error: (err) => {
                    this.isLoading = false;
                    console.error('Failed to fetch attempts for test:', err);
                }
            });
    }

    selectAttempt(index: number): void {
        if (!this.allAttemptsData) return;

        this.selectedAttemptIndex = index;
        const selectedAttempt = this.allAttemptsData.attempts[index];

        this.resultData = {
            attempt_id: selectedAttempt.attempt_id,
            test_id: this.allAttemptsData.test_id,
            student_id: this.allAttemptsData.student_id,
            student_name: this.allAttemptsData.student_name,
            test_type: this.allAttemptsData.test_type,
            status: selectedAttempt.status,
            finished_at: selectedAttempt.finished_at,
            time_taken: selectedAttempt.time_taken,
            is_paid: this.allAttemptsData.is_paid,
            overall_performance: selectedAttempt.overall_performance,
            test_conversation: selectedAttempt.test_conversation,
            retake_permitted: this.allAttemptsData.retake_permitted
        };
        this.isPaid = this.resultData.is_paid || false;
    }

    get currentAttempt(): AttemptByTestId | null {
        if (!this.allAttemptsData || !this.allAttemptsData.attempts) return null;
        return this.allAttemptsData.attempts[this.selectedAttemptIndex];
    }

    get attemptNumber(): number {
        return this.totalAttempts - this.selectedAttemptIndex;
    }

    get totalAttempts(): number {
        return this.allAttemptsData?.attempts?.length || 1;
    }

    formatAttemptDate(dateString: string): string {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    getAttemptNumber(index: number): number {


        return this.totalAttempts - index;
    }

    retakeQuiz(): void {
        if (!this.resultData?.test_id) return;

        this.isLoading = true;
        this.placementService.startAttempt(this.resultData.test_id).subscribe({
            next: (res) => {
                this.isLoading = false;
                if (res.Success && res.Data) {
                    this.router.navigate(['/student-dashboard/placement-test'], {
                        state: {
                            testId: res.Data.test_id,
                            attemptId: res.Data.attempt_id,
                            questions: res.Data.questions,
                            testTitle: this.resultData?.test_type || 'Placement Test',
                        }
                    });
                } else if (!res.Success) {

                    this.toastr.warning(res.Message || 'Unable to start new attempt', 'Attempt Limit', {
                        timeOut: 5000,
                        progressBar: true
                    });
                }
            },
            error: (err) => {
                this.isLoading = false;
                console.error('Failed to restart attempt', err);

                if (err?.error?.Message) {
                    this.toastr.warning(err.error.Message, 'Attempt Limit', {
                        timeOut: 5000,
                        progressBar: true
                    });
                } else {
                    this.toastr.warning('Unable to start new attempt. Please try again later.', 'Error', {
                        timeOut: 5000,
                        progressBar: true
                    });
                }
            }
        });
    }

    practiceAnother(): void {
        this.router.navigate(['/student-dashboard/placement-test-type']);
    }

    goToDashboard(): void {
        this.router.navigate(['/student-dashboard/placement-prep'], {
            queryParams: { tab: 'history' }
        });
    }

    goBack(): void {
        this.goToDashboard();
    }

    get obtainedMarks(): number {
        return this.resultData?.overall_performance?.obtained_marks || 0;
    }

    get totalMarks(): number {
        return this.resultData?.overall_performance?.total_marks || 0;
    }

    get scorePercentage(): number {
        return this.resultData?.overall_performance?.score_percentage || 0;
    }

    get knowledgeLevel(): string {
        const level = this.resultData?.overall_performance?.overall_knowledge_level || 'beginner';
        return level.charAt(0).toUpperCase() + level.slice(1);
    }

    get timeTaken(): string {
        const totalSeconds = this.resultData?.time_taken || 0;
        if (totalSeconds < 60) {
            return `${totalSeconds}s`;
        }
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}m ${seconds}s`;
    }

    get difficulty(): string {

        if (this.allAttemptsData?.difficulty) {
            const diff = this.allAttemptsData.difficulty;
            return diff.charAt(0).toUpperCase() + diff.slice(1).toLowerCase();
        }

        if (this.resultData?.test_conversation?.length && this.resultData.test_conversation.length > 0) {
            const firstQuestion = this.resultData.test_conversation[0].question;
            const diff = 'difficulty_level' in firstQuestion ? firstQuestion.difficulty_level : '';
            if (diff) {
                return diff.charAt(0).toUpperCase() + diff.slice(1);
            }
        }
        return 'Medium';
    }

    get totalQuestions(): number {
        return this.resultData?.overall_performance?.total_questions || 0;
    }

    get correctAnswers(): number {
        return this.resultData?.overall_performance?.correct_answers || 0;
    }

    get incorrectAnswers(): number {
        return this.resultData?.overall_performance?.incorrect_answers || 0;
    }

    get skippedAnswers(): number {
        return this.resultData?.overall_performance?.skipped_answers || 0;
    }

    get overallFeedback(): string {
        return this.resultData?.overall_performance?.overall_feedback || 'Good effort! Focus on understanding core concepts and practicing regularly to improve your score.';
    }

    get scoreProgressValue(): number {

        if (this.totalMarks === 0) return 0;
        return (this.obtainedMarks / this.totalMarks) * 100;
    }

    get isRetakePermitted(): boolean {
        // Prioritize resultData if available (direct from submit), otherwise fallback to allAttemptsData
        if (this.resultData?.retake_permitted !== undefined) {
            return this.resultData.retake_permitted;
        }
        return this.allAttemptsData?.retake_permitted || false;
    }

    viewDetailedResults(): void {

    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

}
